import React, { useState, useRef, useEffect } from "react";
import { SuperTeamPresence } from "@/components/admin/SuperTeamPresence";
import { tr } from '@/i18n/tr';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useIndustry } from "@/context/IndustryContext";
import { useCountry } from "@/context/CountryContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { themeIconUrl } from "@/utils/theme";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Store, Package, Warehouse, Users, ShoppingCart, BarChart3,
  Settings, LogOut, Menu, X, Home, Building2, Truck, CreditCard,
  Globe, DollarSign, FileText, AlertCircle, ClipboardList, ArrowLeftRight,
  Tag, Calendar, RotateCcw, TrendingUp, Shield, Megaphone, Banknote, AlertTriangle,
  ChevronDown, ChevronRight, Clock, Target, Box, Bell, Monitor, ShoppingBag,
  HelpCircle, Receipt, Video, KeyRound, Mail, Award, Layers, Share2, Printer, PackageMinus, TrendingDown, Activity, ShieldCheck, QrCode, Sparkles, MessageCircle, Crown, Bitcoin, Smartphone, Bookmark, Camera, Bike, UtensilsCrossed, Shirt, RefreshCw, ChefHat, Gift, Boxes, Send, Scale, Ticket, Landmark, LifeBuoy, ArrowUp, Star, Trophy, Wallet, Ship, Factory, BookOpen, ShieldAlert } from "lucide-react";
import axios, { API } from "@/lib/api";
import SyncQueueBadge from "@/components/SyncQueueBadge";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import AIButlerWidget from "@/components/AIButlerWidget";
import SuperAIWidget from "@/components/SuperAIWidget";
import { toast } from "sonner";
import TrialConversionBanner from "./TrialConversionBanner";
import PlanStatusBar from "./PlanStatusBar";
import FxAlertBanner from "./FxAlertBanner";
import WaDownBanner from "./WaDownBanner";
import SubscriptionExpiryModal from "./SubscriptionExpiryModal";
import { shopUrl, setCachedShopSlug } from "@/utils/shopUrl";
import { ReleaseNotesModal } from "@/components/ReleaseNotesModal";
import OnlineOrderNotifier from "@/components/pos/OnlineOrderNotifier";
import StockBlockNotifier from "@/components/admin/StockBlockNotifier";
import WholesalePendingNotifier from "@/components/admin/WholesalePendingNotifier";
import StocktakeApprovalNotifier from "@/components/admin/StocktakeApprovalNotifier";
import VendedorChangeNotifier from "@/components/admin/VendedorChangeNotifier";
import StocktakeResultNotifier from "@/components/pos/StocktakeResultNotifier";
import { VersionBadge } from "@/components/VersionBadge";

const AdminLayout = ({ children }) => {
  const { t, lang, changeLang } = useLang();
  const { user, logout, permissions, hasPermission } = useAuth();
  const { uiProfile, industry, aiEnabled } = useIndustry();
  const { is_ve } = useCountry();
  // 🏷 Industry-aware label overrides: rename common menu items so the boss
  // sees the right word for their business (餐馆 → 菜品 instead of 商品).
  const indLabel = (key) => {
    const m = {
      products: { restaurant: t('tplProductsRestaurant'), fast_food: t('tplProductsFastFood'), clothing: t('tplProductsClothing'), pharmacy: t('tplProductsPharmacy'), beauty: t('tplProductsBeauty') },
      categories: { restaurant: t('tplCategoriesRestaurant'), clothing: t('tplCategoriesClothing'), beauty: t('tplCategoriesBeauty') },
      sales: { restaurant: t('tplSalesRestaurant'), beauty: t('tplSalesBeauty') },
    };
    return m[key]?.[industry] || null;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const contentRef = useRef(null);
  // Some pages scroll the window/body instead of the content div — listen to both
  // Cache the friendly shop slug so shopUrl() links use the new URL
  const [, setShopSlugVer] = React.useState(0);
  useEffect(() => {
    if (!user?.tenant_id) return;
    axios.get(`${API}/settings/shop-slug`)
      .then(r => {
        // Guard against cross-tenant cache pollution (stale token during account switch)
        if (r.data.tenant_id && r.data.tenant_id !== user.tenant_id) return;
        setCachedShopSlug(user.tenant_id, r.data.slug);
        setShopSlugVer(v => v + 1);
      })
      .catch(() => {});
  }, [user?.tenant_id]);

  useEffect(() => {
    const onWinScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > 300) setShowScrollTop(true);
      else if ((contentRef.current?.scrollTop || 0) <= 300) setShowScrollTop(false);
    };
    window.addEventListener('scroll', onWinScroll, { passive: true });
    return () => window.removeEventListener('scroll', onWinScroll);
  }, []);
  const scrollAllTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Keep sidebar menu scroll position across page navigations (layout remounts per route)
  const sideNavRef = useRef(null);
  useEffect(() => {
    try {
      const s = parseFloat(sessionStorage.getItem("sx_sidebar_scroll") || "0");
      if (sideNavRef.current && s > 0) sideNavRef.current.scrollTop = s;
    } catch {}
  }, []);
  // Main content always starts at the top when switching pages
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, [location.pathname]);
  // Menu groups start COLLAPSED (boss request 2026-09-20: "后台功能表全部收起来, 租户自己弹开");
  // only 首页 + the group of the current page open by default. Choices persist per device.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sellox_nav_open') || '{}'); } catch (e) { return {}; }
  });
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [visionEnabled, setVisionEnabled] = useState(false);
  // 🎂 Today's birthday count — drives the red dot on 营销 menu item
  const [bdayToday, setBdayToday] = useState(0);
  // 🍽️ Industry features (drives whether 桌位管理 appears in nav)
  const [industryFeatures, setIndustryFeatures] = useState({});

  React.useEffect(() => {
    // Fetch tenant info once to know lifetime_active status (for hiding buyout menu) + plan gates (网店)
    if (!user) return;
    axios.get(`${API}/auth/me`).then(r => setTenantInfo(r.data?.tenant || null)).catch(() => {});
    // Fetch today's birthday count for the menu badge (admin/owner/manager only — endpoint enforces)
    const role = user?.role || "";
    if (["admin", "owner", "manager"].includes(role)) {
      axios.get(`${API}/marketing/upcoming-birthdays?days=0`)
        .then((r) => setBdayToday((r.data?.items || []).filter((c) => c.days_until === 0).length))
        .catch(() => {});
    }
    // Fetch industry status (lightweight — controls 桌位管理 visibility)
    axios.get(`${API}/industry/status`)
      .then((r) => { setIndustryFeatures(r.data?.features || {}); setVisionEnabled(!!r.data?.vision_enabled); })
      .catch(() => {});
  }, [user]);

  const lifetimeActive = !!tenantInfo?.lifetime_active;
  // 📦 wholesale nudge badge on the 🏭 menu group — until the boss opens the wholesale page once
  const [wsBadge, setWsBadge] = useState(false);
  React.useEffect(() => {
    if (!user || !['owner', 'admin', 'manager', 'store_manager'].includes(user.role) || localStorage.getItem('ws_hint_opened')) return;
    axios.get(`${API}/wholesale-hint`).then((r) => setWsBadge(!!r.data?.show)).catch(() => {});
    const off = () => setWsBadge(false);
    window.addEventListener('wholesale-hint-activated', off);
    return () => window.removeEventListener('wholesale-hint-activated', off);
  }, [user]);
  React.useEffect(() => {
    if (location.pathname.startsWith('/admin/wholesale')) { localStorage.setItem('ws_hint_opened', '1'); setWsBadge(false); }
  }, [location.pathname]);
  const shopAllowed = (tenantInfo || user?.tenant)?.has_online_shop !== false;

  const handleChangePassword = async () => {
    if (!pwdForm.old_password || !pwdForm.new_password) return;
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error(t('passwordMismatch'));
      return;
    }
    setPwdLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, { old_password: pwdForm.old_password, new_password: pwdForm.new_password });
      toast.success(t('passwordChanged'));
      setShowChangePwd(false);
      setPwdForm({ old_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setPwdLoading(false);
    }
  };

  // Accordion: opening one group closes the others (boss 2026-09-20 "开另外一个要自己缩回去")
  const toggleGroup = (key) => setOpenGroups(prev => {
    const willOpen = !(prev[key] ?? navGroupsRef.current[key]);
    const next = {};
    Object.keys(navGroupsRef.current).forEach(k => { next[k] = k === 'main' ? (prev[k] ?? true) : false; });
    next[key] = willOpen;
    try { localStorage.setItem('sellox_nav_open', JSON.stringify(next)); } catch (e) { /* ignore */ }
    return next;
  });
  const navGroupsRef = React.useRef({}); // key -> currently-active (auto-open) flag

  const isSuperAdmin = user?.role === 'admin' && !user?.tenant_id;

  const userRole = permissions?.role || user?.role || 'staff';
  const p = permissions?.permissions || {};
  const isAdminOrOwner = ['admin', 'owner'].includes(userRole);
  const isManager = userRole === 'manager';
  const isStoreManager = userRole === 'store_manager';
  const isWholesaleClerk = userRole === 'wholesale_clerk';
  const mayorShopUrl = user?.tenant_id ? `${shopUrl(user.tenant_id)}/mayor` : "";

  const navGroups = isSuperAdmin ? [
    { key: "main", label: t('dashboard'), items: [
      { icon: Home, label: t('dashboard'), path: "/admin" },
    ]},
    { key: "global", label: "🌍 Global", items: [
      { icon: Globe, label: tr('x_39400df4'), path: "/admin/super-country" },
      { icon: Activity, label: "📈 官网流量分析", path: "/admin/super-traffic" },
      { icon: Bitcoin, label: tr('x_ef8318a7'), path: "/admin/super-crypto-income" },
      { icon: Gift, label: tr('refCommNav'), path: "/admin/super-referral-commissions" },
      { icon: Wallet, label: tr('duNav'), path: "/admin/super-direct-pay" },
      { icon: Banknote, label: "学分充值审核", path: "/admin/super-manual-topups" },
      { icon: Sparkles, label: "✨ AI使用统计", path: "/admin/super-ai-usage" },
      { icon: Bell, label: tr('x_09636666'), path: "/admin/super-subscriptions" },
      { icon: Ticket, label: "🎫 租户工单", path: "/admin/super-tickets" },
      { icon: Bell, label: "✨ 版本更新公告", path: "/admin/super-release-notes" },
      { icon: Users, label: tr('x_542ce2f8'), path: "/admin/trial-signups" },
      { icon: CreditCard, label: tr('x_408009cf'), path: "/admin/intl-payment" },
    ]},
    { key: "system", label: t('systemSettings'), items: [
      { icon: Building2, label: t('saasManagement'), path: "/admin/tenants" },
      { icon: Activity, label: "🛰️ 运维台 / Ops", path: "/admin/ops" },
      { icon: DollarSign, label: "💎 套餐价格 / Planes", path: "/admin/saas-plans" },
      { icon: Smartphone, label: "📱 WhatsApp 平台连接", path: "/admin/whatsapp-web" },
      { icon: Activity, label: t('siteMonitor') || 'Site Monitor', path: "/admin/monitor" },
      { icon: HelpCircle, label: t('adminManual'), path: "/admin/super-admin-help" },
    ]},
  ] : isWholesaleClerk ? [
    // 🏭 批发开单员 — only wholesale ordering + online orders
    { key: "wholesale", label: "🏭 " + t('wholesale'), items: [
      { icon: Truck, label: t('createWholesaleOrder') || t('wholesale'), path: "/admin/wholesale" },
      { icon: Globe, label: t('onlineOrders'), path: "/admin/online-orders" },
      { icon: Users, label: t('customerManagement'), path: "/admin/customers" },
      ...(mayorShopUrl ? [{ icon: ShoppingBag, label: lang === 'zh' ? "🛒 批发网店" : "🛒 Tienda Mayorista", href: mayorShopUrl }] : []),
    ]},
  ] : [
    { key: "main", label: t('dashboard'), items: [
      { icon: Home, label: t('dashboard'), path: "/admin" },
      { icon: Bell, label: t('notifications'), path: "/admin/notifications" },
    ]},
    ...(p.can_view_reports || isAdminOrOwner || isManager || isStoreManager ? [{ key: "sales", label: indLabel("sales") || t('salesManagement'), items: [
      { icon: ShoppingCart, label: indLabel("sales") || t('salesManagement'), path: "/admin/sales" },
      { icon: Clock, label: t('shiftReports'), path: "/admin/shift-reports" },
      { icon: Clock, label: lang === 'zh' ? '🧮 结班对账' : '🧮 Conciliación turnos', path: "/admin/shift-reconcile" },
      { icon: Calendar, label: t('dailySettlement'), path: "/admin/daily-settlement" },
      { icon: Calendar, label: t('pointsMonthly'), path: "/admin/points-monthly" },
      { icon: ShieldCheck, label: t('reconciliationMenu'), path: "/admin/reconciliation" },
      { icon: RotateCcw, label: t('refundHistory'), path: "/admin/refunds" },
      ...(!uiProfile.menu_hide_apartados ? [
        { icon: Bookmark, label: "🏷️ " + t('apartadosMenu'), path: "/admin/apartados" },
      ] : []),
      { icon: Camera, label: "📸 " + t('cameraBossMenu'), path: "/boss/camera" },
    ]}] : []),
    ...((p.can_view_reports || p.can_manage_customers || isAdminOrOwner || isManager || isStoreManager) && shopAllowed ? [{ key: "shop", label: lang === 'zh' ? "🛒 网店" : "🛒 Tienda Online", items: [
      { icon: Globe, label: t('onlineOrders'), path: "/admin/online-orders" },
      { icon: Users, label: tr('gb_title'), path: "/admin/group-buy" },
      { icon: Bike, label: "🛵 " + t('deliveryMenu'), path: "/admin/delivery" },
      { icon: Star, label: "⭐ " + (localStorage.getItem("app_lang") === "zh" ? "顾客评价" : "Calificaciones"), path: "/admin/ratings" },
      { icon: QrCode, label: t('shopQRPoster') || tr('x_db29972a'), path: "/admin/shop-qr-poster" },
      { icon: Globe, label: lang === 'zh' ? "🗺️ Google地图入驻" : "🗺️ Google Maps", path: "/admin/google-maps" },
      { icon: Trophy, label: lang === 'zh' ? "🏆 推荐排行榜 (网店)" : "🏆 Ranking Referidos (Tienda)", path: "/admin/referrals" },
      ...(isAdminOrOwner ? [{ icon: Gift, label: tr('refProgramNav'), path: "/admin/referral-program" }] : []),
      { icon: Activity, label: lang === 'zh' ? "📊 网店访客分析" : "📊 Visitas Tienda", path: "/admin/shop-analytics" },
    ]}] : []),
    ...((isAdminOrOwner || isManager || isStoreManager) && visionEnabled ? [{ key: "vision", label: lang === 'zh' ? "📷 AI 监控" : "📷 Cámaras IA", items: [
      { icon: Camera, label: lang === 'zh' ? "📷 实时人数 / 客流 / 报警" : "📷 Personas / Tráfico / Alertas", path: "/admin/vision" },
    ]}] : []),
    ...((p.can_view_reports || p.can_manage_customers || isAdminOrOwner || isManager || isStoreManager) && !uiProfile.menu_hide_wholesale ? [{ key: "wholesale", label: "🏭 " + t('wholesale'), items: [
      { icon: Truck, label: t('wholesale'), path: "/admin/wholesale" },
      ...(mayorShopUrl ? [{ icon: ShoppingBag, label: lang === 'zh' ? "🛒 批发网店" : "🛒 Tienda Mayorista", href: mayorShopUrl }] : []),
      { icon: Building2, label: t('franchiseHQ'), path: "/admin/b2b-portal" },
      ...(!uiProfile.menu_hide_field_sales ? [
        { icon: Package, label: tr('pk_menu'), path: "/admin/picking" },
      ] : []),
      { icon: Users, label: lang === 'zh' ? '🤝 加盟管理' : '🤝 Franquicias', path: "/admin/franchise" },
    ]}] : []),
    ...(p.can_manage_products || isAdminOrOwner ? [{ key: "products", label: indLabel("products") || t('productManagement'), items: [
      { icon: Package, label: indLabel("products") || t('productManagement'), path: "/admin/products" },
      { icon: Tag, label: indLabel("categories") || t('categoryManagement'), path: "/admin/categories" },
      { icon: Tag, label: lang === 'zh' ? '🏷️ 改价单' : '🏷️ Cambios de precio', path: "/admin/price-changes" },
      { icon: Box, label: t('bundles'), path: "/admin/bundles" },
      { icon: Tag, label: t('priceCheck') || tr('x_bb58400a'), path: "/price-check" },
      ...(industryFeatures?.size_color_variants === true ? [
        { icon: Shirt, label: "👕 " + t('clothingVariantsMenu'), path: "/admin/clothing-variants" }
      ] : []),
    ]}] : []),
    ...(p.can_manage_inventory || isAdminOrOwner ? [{ key: "inventory", label: t('warehouseManagement'), items: [
      { icon: Warehouse, label: t('warehouseManagement'), path: "/admin/warehouses" },
      ...(!uiProfile.menu_hide_transfers ? [
        { icon: ArrowLeftRight, label: t('transferManagement'), path: "/admin/transfers" },
      ] : []),
      { icon: AlertCircle, label: t('stockAlerts'), path: "/admin/stock-alerts" },
      { icon: Package, label: t('smartRestock'), path: "/admin/smart-restock" },
      ...(uiProfile.show_lot_tracking ? [
        { icon: Boxes, label: t('lotsMenuLabel') || tr('x_5910639f'), path: "/admin/lots" },
      ] : []),
      { icon: ClipboardList, label: t('stockTaking'), path: "/admin/stock-taking" },
      { icon: PackageMinus, label: t('stockLoss') || tr('x_8f2328a2'), path: "/admin/stock-loss" },
      { icon: TrendingDown, label: "🕵️ " + tr('sx_shrink_menu'), path: "/admin/shrinkage" },
    ]}] : []),
    ...(p.can_manage_purchases || isAdminOrOwner ? [{ key: "purchase", label: t('purchaseManagement'), items: [
      { icon: FileText, label: t('purchaseManagement'), path: "/admin/purchases" },
      { icon: RotateCcw, label: t('purchaseReturn'), path: "/admin/purchase-returns" },
      { icon: Building2, label: t('supplierManagement'), path: "/admin/suppliers" },
      { icon: DollarSign, label: lang === 'zh' ? '💰 应付对账' : '💰 Cuentas por pagar', path: "/admin/payables" },
      { icon: Receipt, label: lang === 'zh' ? '🧾 收款单/付款单' : '🧾 Recibos y pagos', path: "/admin/vouchers" },
      { icon: Ship, label: lang === 'zh' ? '🚢 报关单·到岸成本' : '🚢 Aduana · costo en tierra', path: "/admin/customs" },
      { icon: Factory, label: lang === 'zh' ? '🏭 工厂 · 配方/生产' : '🏭 Fábrica · recetas/producción', path: "/admin/fabrica" },
    ]}] : []),
    ...(p.can_manage_customers || isAdminOrOwner || isStoreManager ? [{ key: "crm", label: t('customerManagement'), items: [
      { icon: Users, label: t('customerManagement'), path: "/admin/customers" },
      { icon: Award, label: t('loyaltyProgram'), path: "/admin/loyalty" },
      { icon: Award, label: "📱 " + t('pointsQrMenu'), path: "/admin/points-qr" },
      { icon: ShoppingBag, label: t('pointsMall'), path: "/admin/points-mall" },
      { icon: CreditCard, label: t('creditManagement'), path: "/admin/credits" },
      { icon: Gift, label: t('tplMenuGiftCards'), path: "/admin/gift-cards" },
    ]}] : []),
    // 🍽️ Restaurant / 💅 Beauty industry groups (conditional)
    ...((p.can_manage_customers || isAdminOrOwner || isStoreManager) && industryFeatures?.tables_enabled === true ? [{ key: "resto", label: lang === 'zh' ? "🍽️ 餐厅" : "🍽️ Restaurante", items: [
      { icon: UtensilsCrossed, label: t('tplMenuTables'), path: "/admin/restaurant-tables" },
      { icon: ChefHat, label: t('tplMenuKds'), path: "/admin/kds" },
      { icon: Megaphone, label: "📺 Llamador (取餐屏)", path: "/llamador" },
    ]}] : []),
    ...((p.can_manage_customers || isAdminOrOwner || isStoreManager) && uiProfile?.show_appointment === true ? [{ key: "beauty", label: t('tplMenuAppointments'), items: [
      { icon: Calendar, label: t('tplMenuAppointments'), path: "/admin/appointments" },
      { icon: Ticket, label: "🎫 " + tr('sx_menu_packages'), path: "/admin/service-packages" },
      { icon: Sparkles, label: "💆 " + tr('x_beauty_center'), path: "/admin/beauty" },
    ]}] : []),
    ...(p.can_view_reports || isAdminOrOwner || isManager || isStoreManager ? [{ key: "finance", label: "📈 " + t('reports'), items: [
      { icon: BarChart3, label: "📊 " + (t('reportsHubMenu') || t('analyticsCenter')), path: "/admin/analytics" },
      { icon: TrendingUp, label: "🏆 " + (lang === 'zh' ? '畅销排行' : 'Top Ventas'), path: "/admin/top-sellers" },
      { icon: TrendingUp, label: "🐢 " + (lang === 'zh' ? '滞销排行' : 'Menos Vendidos'), path: "/admin/slow-movers" },
      { icon: TrendingUp, label: t('profitAnalysis'), path: "/admin/profit-analysis" },
      { icon: Target, label: t('salesTarget'), path: "/admin/sales-targets" },
      { icon: Banknote, label: t('accountsReceivable'), path: "/admin/accounts" },
      { icon: Landmark, label: "🏦 " + (t('bankLedgerMenu') || (lang === 'zh' ? '银行账务' : 'Cuentas y Bancos')), path: "/admin/bank-accounts" },
      ...(is_ve ? [{ icon: Landmark, label: "💳 " + (lang === 'zh' ? 'Cashea 对账' : 'Conciliación Cashea'), path: "/admin/cashea" }] : []),
      { icon: TrendingDown, label: t('expenseManagement') || tr('x_3fef31de'), path: "/admin/expenses" },
      ...(is_ve ? [
        { icon: BookOpen, label: "📒 " + (lang === 'zh' ? '会计（虚拟会计师）' : 'Contabilidad'), path: "/admin/contabilidad" },
        { icon: ShieldAlert, label: "🕵️ " + (lang === 'zh' ? '收银行为分析' : 'Comportamiento de caja'), path: "/admin/caja-ebr" },
        { icon: DollarSign, label: t('exchangeRates'), path: "/admin/exchange-rates" },
        { icon: Shield, label: "SENIAT Fiscal", path: "/admin/fiscal" },
        { icon: Shield, label: "🧾 " + t('einv_menu'), path: "/admin/einvoice" },
      ] : []),
    ]}] : []),
    ...(p.can_view_reports || p.can_manage_settings || isAdminOrOwner || isManager ? [{ key: "marketing", label: lang === 'zh' ? "📢 营销" : "📢 Marketing", items: [
      { icon: Megaphone, label: t('promotions'), path: "/admin/promotions" },
      { icon: Megaphone, label: lang === 'zh' ? "📣 网店营销包" : "📣 Marketing tienda online", path: "/admin/shop-marketing" },
      { icon: Ticket, label: lang === 'zh' ? "🎰 大奖抽奖" : "🎰 Gran Sorteo", path: "/admin/sorteo" },
      { icon: Send, label: "📢 " + t('marketingBlastMenu'), path: "/admin/marketing-blast" },
      { icon: Target, label: t('rfmMenuLabel') || tr('x_b1381d19'), path: "/admin/rfm" },
      { icon: Mail, label: "Marketing", path: "/admin/marketing" },
      { icon: Smartphone, label: "📱 " + t('waConnectMenu'), path: "/admin/whatsapp-web" },
      { icon: MessageCircle, label: "📲 " + t('waOutboxMenu'), path: "/admin/whatsapp-outbox" },
    ]}] : []),
    ...(p.can_manage_employees || isAdminOrOwner || isStoreManager ? [{ key: "hr", label: t('employees'), items: [
      { icon: Users, label: t('employees'), path: "/admin/employees" },
      { icon: Clock, label: t('attendance'), path: "/admin/attendance" },
      { icon: DollarSign, label: t('commission'), path: "/admin/commission" },
      { icon: Award, label: t('staffPerfMenu') || tr('x_559dca3c'), path: "/admin/staff-performance" },
      ...(isAdminOrOwner || isManager || isStoreManager ? [{ icon: Shield, label: lang === 'zh' ? "⚖️ 员工法务" : "⚖️ RRHH Legal", path: "/admin/rrhh-legal" }] : []),
    ]}] : []),
    ...(p.can_manage_settings || isAdminOrOwner ? [{ key: "system", label: t('systemSettings'), items: [
      { icon: Store, label: t('storeManagement'), path: "/admin/stores" },
      { icon: CreditCard, label: t('paymentSettings'), path: "/admin/payment-settings" },
      { icon: Sparkles, label: "✨ " + t('aiCreditsMenu'), path: "/admin/ai-credits" },
      { icon: RefreshCw, label: "☁️ " + t('syncQueueMenu'), path: "/admin/sync-queue" },
      { icon: Mail, label: t('emailReports'), path: "/admin/email-reports" },
      { icon: Settings, label: t('systemSettings'), path: "/admin/settings" },
      { icon: Printer, label: lang === "zh" ? "🎨 单据设计" : "🎨 Diseño de documentos", path: "/admin/doc-design" },
      { icon: Shield, label: t('auditLog'), path: "/admin/audit-log" },
      ...(isAdminOrOwner ? [{ icon: ShieldCheck, label: t('trustedDevicesMenu'), path: "/admin/trusted-devices" }] : []),
      ...(is_ve ? [
        { icon: Bell, label: "⚠️ " + t('fxAlertsMenu'), path: "/admin/fx-alerts" },
      ] : []),
      { icon: ShieldCheck, label: "🔐 " + t('bossPinMenu'), path: "/admin/boss-pin" },
      { icon: LifeBuoy, label: "🛠 " + (lang === 'zh' ? '技术支持' : 'Soporte Técnico'), path: "/admin/support" },
      { icon: Video, label: "🎬 " + (lang === 'zh' ? '视频教程' : 'Video tutoriales'), path: "/admin/tutorials" },
      { icon: HelpCircle, label: t('userManual'), path: "/manual" },
    ]}] : []),
  ];

  // Plain JSX (not an inline component): an inline component type is re-created on every render,
  // remounting the whole sidebar and wiping child state (ThemeSwitcher panel closed instantly).
  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2.5">
          <img src={themeIconUrl()} alt="Sellox" className="w-8 h-8 rounded-lg sx-logo3d" />
          <h2 className="text-lg font-bold text-white tracking-wide">Sellox</h2>
        </div>
        <p className="text-xs text-slate-400 mt-1">{user?.name || user?.username}</p>
        {user?.tenant_id && <p className="text-xs text-blue-400 mt-0.5">{user?.tenant_name || user?.tenant_id}</p>}
        {isSuperAdmin && <Badge className="mt-1 bg-amber-500/20 text-amber-400 text-xs">{t('superAdmin')}</Badge>}
        {isSuperAdmin && <SuperTeamPresence me={user?.username} />}
      </div>
      <nav ref={sideNavRef} onScroll={(e) => { try { sessionStorage.setItem("sx_sidebar_scroll", String(e.target.scrollTop)); } catch {} }} className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {/* Quick Access: POS & Shop */}
        {!isSuperAdmin && (
        <div className="flex gap-2 px-1 pb-2 mb-2 border-b border-slate-700">
          {!isWholesaleClerk && (
          <Link to="/pos" className="flex-1" onClick={() => setSidebarOpen(false)}>
            <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-sm h-9 gap-1.5" data-testid="quick-pos-btn">
              <Monitor className="w-4 h-4" /> POS
            </Button>
          </Link>
          )}
          {user?.tenant_id ? (shopAllowed && (
            <a href={shopUrl(user.tenant_id)} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-sm h-9 gap-1.5" data-testid="quick-shop-btn">
                <ShoppingBag className="w-4 h-4" /> {t('shop')}
              </Button>
            </a>
          )) : (
            <Link to="/admin/tenants" className="flex-1" onClick={() => setSidebarOpen(false)}>
              <Button variant="outline" className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-sm h-9 gap-1.5" data-testid="quick-shop-btn">
                <ShoppingBag className="w-4 h-4" /> {t('shops')}
              </Button>
            </Link>
          )}
        </div>
        )}
        {navGroups.map(group => {
          const isActive = group.items.some(item => location.pathname === item.path);
          navGroupsRef.current[group.key] = group.key === 'main' || isActive;
          const isExpanded = openGroups[group.key] ?? (group.key === 'main' || isActive);
          return (
            <div key={group.key}>
              <button onClick={() => toggleGroup(group.key)} data-testid={`nav-group-${group.key}`} className={`sx-nav3d w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${isActive ? 'text-emerald-400 bg-emerald-500/5' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                <span className="font-medium">{group.label}{group.key === 'wholesale' && wsBadge && <span className="relative inline-flex ml-2 align-middle" data-testid="nav-wholesale-new-badge"><span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" /><span className="relative inline-flex rounded-full bg-amber-400 text-[9px] font-bold text-slate-900 px-1.5 leading-4">NEW</span></span>}</span>
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {isExpanded && (
                <div className="ml-2 space-y-0.5 mt-0.5">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    if (item.href) {
                      return (
                        <a key={item.href} href={item.href} target="_blank" rel="noopener noreferrer" className="sx-nav3d flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-slate-400 hover:text-white hover:bg-slate-700/30" data-testid="nav-mayor-shop">
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </a>
                      );
                    }
                    const active = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className={`sx-nav3d flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors ${active ? 'sx-nav3d-active bg-emerald-500/15 text-emerald-400 font-medium' : 'text-slate-400 hover:text-white hover:bg-slate-700/30'}`} data-testid={`nav-${item.path.replace('/admin/', '').replace('/', '-') || 'home'}`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                        {item.path === "/admin/whatsapp-web" && bdayToday > 0 && (
                          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-500 text-white text-[10px] font-bold animate-pulse" data-testid="nav-bday-badge" title={`${tr('x_296304f1')} ${bdayToday} ${tr('x_d8886c56')}`}>
                            🎂{bdayToday}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-700 space-y-2">
        <SyncQueueBadge />
        <ThemeSwitcher block />
        <select value={lang} onChange={(e) => changeLang(e.target.value)} className="w-full bg-slate-700 border-slate-600 text-white rounded px-2 py-1.5 text-sm" data-testid="lang-select">
          <option value="zh">{'中文'}</option><option value="en">English</option><option value="es">Español</option>
        </select>
        {isSuperAdmin && <Link to="/admin-manual-6688" className="block text-center text-[10px] text-slate-600 hover:text-slate-400 transition-colors py-0.5" data-testid="secret-manual-link">v2.0.6</Link>}
        <Button variant="ghost" onClick={() => setShowChangePwd(true)} className="w-full justify-start text-slate-300 hover:bg-slate-700 hover:text-white" data-testid="change-password-btn"><KeyRound className="w-4 h-4 mr-2" />{t('changePassword')}</Button>
        <Button variant="ghost" onClick={() => { logout(); navigate("/login"); }} className="w-full justify-start text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />{t('logout')}</Button>
        <VersionBadge variant="admin" />
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-slate-900 flex ${user?.role === 'admin' && !user?.tenant_id ? 'sx-super' : ''}`}>
      <ReleaseNotesModal lang={lang} />
      <ReadonlyBanner />
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`sx-side3d fixed inset-y-0 left-0 z-50 w-56 h-screen bg-slate-800/95 backdrop-blur-sm border-r border-slate-700/80 transform transition-transform lg:sticky lg:top-0 lg:self-start lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {sidebarContent}
      </aside>
      {/* Main */}
      {/* 📱 Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-50 h-16 bg-slate-900/95 backdrop-blur-xl border-t border-slate-800 md:hidden flex" data-testid="mobile-bottom-nav">
        {[
          { icon: Home, label: t('dashboard') || "首页", path: "/admin" },
          { icon: ShoppingCart, label: t('salesManagement') || "销售", path: "/admin/sales" },
          { icon: Package, label: t('products') || "商品", path: "/admin/products" },
          { icon: TrendingUp, label: "Top", path: "/admin/top-sellers" },
        ].map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} data-testid={`bottom-nav${item.path.replace(/\//g, '-')}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 active:bg-slate-800 ${active ? "text-emerald-400" : "text-slate-400"}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] leading-none truncate max-w-[70px]">{item.label}</span>
            </Link>
          );
        })}
        <button onClick={() => setSidebarOpen(true)} data-testid="bottom-nav-more"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-slate-400 active:bg-slate-800">
          <Menu className="w-5 h-5" />
          <span className="text-[10px] leading-none">{t('more') || "更多 Más"}</span>
        </button>
      </nav>
      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <header className="bg-slate-800/90 backdrop-blur-sm border-b border-slate-700/80 px-4 py-3 flex items-center justify-between lg:hidden sticky top-0 z-30">
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} className="text-white"><Menu className="w-5 h-5" /></Button>
          <div className="flex items-center gap-2">
            <SyncQueueBadge />
            <span className="text-white font-medium">Sellox</span>
          </div>
          <Link to="/pos"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-sm">{t('posMode')}</Button></Link>
        </header>
        <div ref={contentRef} onScroll={(e) => setShowScrollTop(e.target.scrollTop > 300 || (window.scrollY || 0) > 300)} className="p-4 lg:p-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 0px)' }}>
          <PlanStatusBar />
          <TrialConversionBanner />
          <FxAlertBanner />
          <WaDownBanner />
          <SubscriptionExpiryModal />
          {children}
        </div>
        {/* ⬆️ Back to top */}
        {showScrollTop && (
          <button
            onClick={scrollAllTop}
            className="fixed bottom-24 right-4 md:right-7 z-40 w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/30 flex items-center justify-center transition-colors"
            data-testid="scroll-to-top-btn"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePwd} onOpenChange={setShowChangePwd}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle>{t('changePassword')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-xs text-slate-400">{t('oldPassword')}</label><Input type="password" value={pwdForm.old_password} onChange={e => setPwdForm({...pwdForm, old_password: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="old-password-input" /></div>
            <div><label className="text-xs text-slate-400">{t('newPassword')}</label><Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="new-password-input" /></div>
            <div><label className="text-xs text-slate-400">{t('confirmNewPassword')}</label><Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="confirm-password-input" /></div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowChangePwd(false)} className="border-slate-600">{t('cancel')}</Button>
              <Button onClick={handleChangePassword} disabled={pwdLoading} className="bg-emerald-500 hover:bg-emerald-600" data-testid="change-password-submit">{t('save')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* AI butler: mobile shows a smaller button above the bottom nav */}
      <div>
        {isSuperAdmin ? <SuperAIWidget /> : (aiEnabled ? <AIButlerWidget /> : null)}
      </div>
      {!isSuperAdmin && <OnlineOrderNotifier />}
      {/* ❓ floating help widget removed 2026-10-28 (boss: it drifted over the POS/wholesale total) — help lives at /manual + support tickets */}
      {!isSuperAdmin && (['admin', 'manager', 'store_manager'].includes(userRole) || hasPermission('can_manage_inventory')) && <StockBlockNotifier />}
      {!isSuperAdmin && (['admin', 'manager', 'store_manager', 'owner'].includes(userRole) || hasPermission('can_manage_customers')) && <WholesalePendingNotifier />}
      {!isSuperAdmin && ['owner', 'admin'].includes(userRole) && <StocktakeApprovalNotifier />}
      {!isSuperAdmin && ['owner', 'admin', 'manager', 'store_manager'].includes(userRole) && <VendedorChangeNotifier />}
      {!isSuperAdmin && ['manager', 'store_manager'].includes(userRole) && <StocktakeResultNotifier />}
    </div>
  );
};

export default AdminLayout;


// 🔒 shown while a super admin is viewing a tenant back office read-only (see TenantsPage.enterReadonly)
function ReadonlyBanner() {
  let info = null;
  try { info = JSON.parse(sessionStorage.getItem("impersonate_readonly") || "null"); } catch (e) { info = null; }
  if (!info) return null;
  const exit = () => {
    sessionStorage.removeItem("impersonate_readonly"); sessionStorage.removeItem("token"); sessionStorage.removeItem("cached_auth_user");
    window.close();
    setTimeout(() => { window.location.href = "/login"; }, 300);
  };
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-amber-500 text-slate-900 text-sm font-semibold px-4 py-2 flex items-center justify-between gap-3 shadow-2xl" data-testid="readonly-banner">
      <span>🔒 超管只读查看：<b>{info.tenant}</b>（账号 {info.as_user}）· 不能修改任何数据 / Solo lectura</span>
      <button onClick={exit} className="bg-slate-900 text-amber-300 rounded-md px-3 py-1 hover:bg-slate-800" data-testid="readonly-exit">关闭查看 ✕</button>
    </div>
  );
}
